import { Icon, mergeStyles, Stack, Text } from "@fluentui/react";
import React, { useEffect, useState } from "react";

export interface ShareToastProps {
    message: string | null;
    type: "success" | "error";
    onDismiss?: () => void;
}

const toastContainerStyle = mergeStyles({
    position: "fixed",
    top: 80,
    right: 24,
    zIndex: 1000,
    animation: "slideInRight 0.3s ease-out",
    "@keyframes slideInRight": {
        from: {
            transform: "translateX(100%)",
            opacity: 0
        },
        to: {
            transform: "translateX(0)",
            opacity: 1
        }
    }
});

const toastStyle = (type: "success" | "error") =>
    mergeStyles({
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 20px",
        borderRadius: 8,
        backgroundColor: type === "success" ? "#DFF6DD" : "#FDE7E9",
        border: `1px solid ${type === "success" ? "#107C10" : "#A80000"}`,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        minWidth: 280,
        maxWidth: 400
    });

const iconContainerStyle = (type: "success" | "error") =>
    mergeStyles({
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: type === "success" ? "#107C10" : "#A80000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
    });

const dismissButtonStyle = mergeStyles({
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    marginLeft: "auto",
    opacity: 0.6,
    ":hover": {
        opacity: 1
    }
});

export const ShareToast: React.FC<ShareToastProps> = ({ message, type, onDismiss }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (message) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    }, [message]);

    if (!isVisible || !message) return null;

    return (
        <div className={toastContainerStyle}>
            <div className={toastStyle(type)}>
                <div className={iconContainerStyle(type)}>
                    <Icon
                        iconName={type === "success" ? "CheckMark" : "ErrorBadge"}
                        styles={{ root: { color: "white", fontSize: 16 } }}
                    />
                </div>
                <Stack tokens={{ childrenGap: 2 }}>
                    <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: type === "success" ? "#107C10" : "#A80000" } }}>
                        {type === "success" ? "Link Copied!" : "Error"}
                    </Text>
                    <Text variant="small" styles={{ root: { color: "#323130" } }}>
                        {message}
                    </Text>
                </Stack>
                {onDismiss && (
                    <button className={dismissButtonStyle} onClick={onDismiss} aria-label="Dismiss">
                        <Icon iconName="Cancel" styles={{ root: { fontSize: 12, color: "#605E5C" } }} />
                    </button>
                )}
            </div>
        </div>
    );
};

export interface SharedContentBannerProps {
    isSharedContent: boolean;
    onDismiss?: () => void;
}

const bannerContainerStyle = mergeStyles({
    animation: "slideDown 0.3s ease-out",
    "@keyframes slideDown": {
        from: {
            transform: "translateY(-100%)",
            opacity: 0
        },
        to: {
            transform: "translateY(0)",
            opacity: 1
        }
    }
});

const bannerStyle = mergeStyles({
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 20px",
    borderRadius: 8,
    background: "linear-gradient(135deg, #0078D4 0%, #106EBE 100%)",
    marginBottom: 16,
    boxShadow: "0 2px 8px rgba(0, 120, 212, 0.25)"
});

const bannerIconStyle = mergeStyles({
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
});

const bannerDismissStyle = mergeStyles({
    background: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: 4,
    cursor: "pointer",
    padding: "6px 12px",
    marginLeft: "auto",
    color: "white",
    fontSize: 12,
    fontWeight: 500,
    transition: "all 0.2s ease",
    ":hover": {
        background: "rgba(255, 255, 255, 0.2)"
    }
});

export const SharedContentBanner: React.FC<SharedContentBannerProps> = ({ isSharedContent, onDismiss }) => {
    const [isVisible, setIsVisible] = useState(isSharedContent);

    useEffect(() => {
        setIsVisible(isSharedContent);
    }, [isSharedContent]);

    if (!isVisible) return null;

    const handleDismiss = () => {
        setIsVisible(false);
        onDismiss?.();
    };

    return (
        <div className={bannerContainerStyle}>
            <div className={bannerStyle}>
                <div className={bannerIconStyle}>
                    <Icon iconName="Share" styles={{ root: { color: "white", fontSize: 18 } }} />
                </div>
                <Stack tokens={{ childrenGap: 2 }}>
                    <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: "white" } }}>
                        Shared Content
                    </Text>
                    <Text variant="small" styles={{ root: { color: "rgba(255, 255, 255, 0.85)" } }}>
                        This prompt was shared with you. Feel free to modify and run it!
                    </Text>
                </Stack>
                <button className={bannerDismissStyle} onClick={handleDismiss}>
                    Got it
                </button>
            </div>
        </div>
    );
};
